import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Compass, Building2, UserPlus, ShieldCheck, Database, AlertTriangle, ScrollText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useT52 } from "@/i18n/v52";
import { MOD_STATUS } from "@/lib/nav";
import { Dash, Empty52, useCanSee, useFmt } from "@/components/v52/Bits";

const RINGS = [70, 120, 170, 220, 270, 320];
const DOTS = Array.from({ length: 45 }, (_, n) => ({ x: 330 + Math.floor(n / 5) * 26, y: 96 + (n % 5) * 26, o: 0.15 + Math.floor(n / 5) * 0.05 }));

function Ornament() {
  return (
    <svg className="ornament" viewBox="0 0 600 240" preserveAspectRatio="xMaxYMid slice" aria-hidden="true">
      <defs>
        <linearGradient id="f52-arc" x1="0" x2="1" y1="0" y2="0"><stop offset="0" stopColor="#D89A00" stopOpacity="0" /><stop offset="1" stopColor="#D89A00" stopOpacity=".6" /></linearGradient>
        <radialGradient id="f52-glow" cx="0.85" cy="0.9" r="0.6"><stop offset="0" stopColor="#E7B515" stopOpacity=".22" /><stop offset="1" stopColor="#E7B515" stopOpacity="0" /></radialGradient>
      </defs>
      <rect width="600" height="240" fill="url(#f52-glow)" />
      <g fill="none" stroke="url(#f52-arc)" strokeWidth="1.2">{RINGS.map((r) => <circle key={r} cx="540" cy="250" r={r} />)}</g>
      <g fill="#E7B515">{DOTS.map((d) => <circle key={`${d.x}-${d.y}`} cx={d.x} cy={d.y} r="1.6" opacity={d.o} />)}</g>
    </svg>
  );
}

export function Welcome() {
  const { user, env } = useAuth();
  const { t } = useTranslation();
  const { t52, locale } = useT52();
  const first = (user?.name || "").trim().split(/\s+/)[0] || user?.email || "";
  const today = new Date().toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  return (
    <section className="f52-welcome" aria-labelledby="f52-welcome-title" data-testid="dashboard-welcome">
      <Ornament />
      <div>
        <p className="f52-eyebrow">FUNDIO Control OS</p>
        <h1 id="f52-welcome-title" data-testid="dashboard-welcome-title">{t52("welcome", { name: first })}</h1>
        <p className="lead">{t52("welcomeLead")}</p>
      </div>
      <div className="meta" data-testid="dashboard-welcome-meta">
        <strong>{today}</strong>{t(env?.is_production ? "layout.liveProduction" : "layout.previewStaging")}
      </div>
    </section>
  );
}

function ModList({ mods }) {
  const { t52 } = useT52();
  const fmt = useFmt();
  return (
    <div className="f52-group" data-testid="dashboard-moderation-panel">
      <h3>{t52("waModT")}</h3>
      <ul className="f52-mod">
        {mods.map((m) => (
          <li key={m.id} data-testid={`dashboard-mod-${m.id}`}>
            <div><p>{m.title}</p><small>{[m.brocki_name, fmt(m.created_at)].filter(Boolean).join(" · ")}</small></div>
            <span className="f52-chip fail">{MOD_STATUS[m.status]?.label || m.status}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function WorkAreas({ stats, cust }) {
  const { t52 } = useT52();
  const can = useCanSee();
  const pending = stats?.kpis?.pending_moderation;
  const custDesc = !cust || cust.service_available === false ? t52("waCustDown") : cust.total ? t52("waCustN", { n: cust.total }) : t52("waCustNone");
  const modDesc = pending == null ? t52("waModUnk") : pending ? t52("waModN", { n: pending }) : t52("waModNone");
  const areas = [
    { key: "research", to: "/research", icon: Compass, title: "waResearchT", desc: t52("waResearchD"), btn: "waResearchB" },
    { key: "kunden", to: "/brockis", icon: Building2, title: "waCustT", desc: custDesc, btn: "waCustB" },
    { key: "onboarding", to: "/onboarding", icon: UserPlus, title: "waOnbT", desc: t52("waOnbD"), btn: "waOnbB" },
    { key: "moderation", to: "/moderation", icon: ShieldCheck, title: "waModT", desc: modDesc, btn: "waModB", secondary: true },
  ].filter((a) => can(a.key));
  const mods = stats?.recent_moderation || [];
  return (
    <section className="f52-panel" aria-labelledby="f52-h-work" data-testid="dashboard-work-areas">
      <div className="f52-panel-head"><h2 id="f52-h-work">{t52("workAreas")}</h2></div>
      <ul className="f52-work">
        {areas.map(({ key, to, icon: Icon, title, desc, btn, secondary }) => (
          <li key={key} data-testid={`work-area-${key}`}>
            <span className="f52-tile"><Icon aria-hidden="true" /></span>
            <div><h3>{t52(title)}</h3><p data-testid={`work-area-${key}-desc`}>{desc}</p></div>
            <Link to={to} className={`f52-btn ${secondary ? "f52-btn-secondary" : "f52-btn-primary"}`} data-testid={`work-area-${key}-link`}>{t52(btn)}</Link>
          </li>
        ))}
      </ul>
      {mods.length > 0 && can("moderation") && <ModList mods={mods} />}
    </section>
  );
}

function Group({ title, hint, first, testid, children }) {
  return (
    <div className={`f52-group${first ? " first" : ""}`} data-testid={testid}>
      <h3>{title}{hint && <span>{hint}</span>}</h3>
      {children}
    </div>
  );
}

function Vals({ items, five, testid }) {
  return (
    <dl className={`f52-vals${five ? " five" : ""}`} data-testid={testid}>
      {items.map(([label, value, tid, nowrap]) => (
        <div className="f52-val" key={tid} data-testid={tid}><dt>{label}</dt><dd className={nowrap ? "nowrap" : ""}><Dash v={value} /></dd></div>
      ))}
    </dl>
  );
}

function Notice({ text, testid }) {
  return <div className="f52-notice" role="status" data-testid={testid}><AlertTriangle aria-hidden="true" /><span>{text}</span></div>;
}

export function DataOverview({ seg, azm, cantons }) {
  const { t } = useTranslation();
  const { t52 } = useT52();
  const { user, env } = useAuth();
  const isProd = !!env?.is_production;
  const isSuper = user?.role === "super_admin";
  const dir = seg?.brocki_directory || {};
  const cust = seg?.fundio_customers || {};
  const plans = cust.plans || {};
  const sp = azm?.sales_priority || {};
  const d = (k) => (azm ? `kpi-staging-${k}` : `kpi-directory-${k}`);
  const staging = [
    [t("dashboard.researchLocations"), dir.total, d("total")], [t("dashboard.verified"), dir.verified, d("verified")],
    [t("dashboard.readyForImport"), dir.ready_for_import, d("ready")], [t("dashboard.cantonsResearch"), dir.cantons, d("cantons")],
  ];
  return (
    <section className="f52-panel" aria-labelledby="f52-h-data" data-testid="dashboard-data-overview">
      <div className="f52-panel-head">
        <h2 id="f52-h-data">{t52("dataTitle")}</h2>
        <span className="f52-chip-src" data-testid="dashboard-data-source"><Database aria-hidden="true" />{t52(isProd ? "srcProd" : "srcPreview")}</span>
      </div>
      {azm && (
        <Group first title={t("nav.items.az_master")} hint={t52("grpMasterHint")} testid="dashboard-master-group">
          <Vals testid="dashboard-master-kpis" items={[
            [t("dashboard.kpiTotalMaster"), azm.total_master ?? 0, "kpi-master-total"],
            [t("dashboard.kpiStrictActive"), azm.strict_verified_active ?? 0, "kpi-master-strict-active"],
            [t("dashboard.kpiExists"), azm.directory?.EXISTS ?? 0, "kpi-master-exists"],
            [t("dashboard.kpiVerifiedMissing"), azm.directory?.VERIFIED_MISSING ?? 0, "kpi-master-verified-missing"],
            [t("dashboard.kpiNeedsAddress"), azm.directory?.NEEDS_ADDRESS_VERIFICATION ?? 0, "kpi-master-needs-address"],
            [t("dashboard.kpiNeedsResearch"), azm.directory?.NEEDS_RESEARCH ?? 0, "kpi-master-needs-research"],
            [t("dashboard.kpiCantons"), cantons == null ? null : `${cantons}/26`, "kpi-master-cantons"],
            [t("dashboard.kpiSalesABC"), `${sp.A ?? 0} / ${sp.B ?? 0} / ${sp.C ?? 0}`, "kpi-master-sales", true],
          ]} />
        </Group>
      )}
      {!seg ? <Notice text={t52("loadError")} testid="dashboard-segments-error" /> : (
        <>
          <Group first={!azm} title={azm ? t52("grpStaging") : t("dashboard.segDirectoryTitle")} hint={azm ? t("dashboard.stagingTitle") : t("dashboard.segDirectoryHint")}>
            <Vals items={staging} testid={azm ? "dashboard-staging-research" : "dashboard-directory-kpis"} />
          </Group>
          <Group title={t("dashboard.segCustomersTitle")} hint={t("dashboard.segCustomersHint")}>
            {cust.service_available === false ? <Notice text={t52("custDown")} testid="dashboard-customers-unavailable" /> : (
              <Vals five testid="dashboard-customers-kpis" items={[
                [t("dashboard.customersTotal"), cust.total ?? 0, "kpi-customers-total"], [t("dashboard.activeAccounts"), cust.active ?? 0, "kpi-customers-active"],
                ["FREE", plans.FREE ?? 0, "kpi-customers-free"], ["LIGHT", plans.LIGHT ?? 0, "kpi-customers-light"], ["WEBPRO", plans.WEBPRO ?? 0, "kpi-customers-webpro"],
              ]} />
            )}
          </Group>
          {isSuper && !isProd && (
            <Group title={t("dashboard.segQaTitle")} hint={t52("grpQaHint")}>
              <Vals testid="dashboard-qa-kpis" items={[[t("dashboard.demoTenants"), seg.internal_qa?.demo_tenants ?? 0, "kpi-qa-demo-tenants"]]} />
            </Group>
          )}
        </>
      )}
    </section>
  );
}

export function Activity({ stats }) {
  const { t52 } = useT52();
  const can = useCanSee();
  const fmt = useFmt();
  const rows = stats?.recent_audit || [];
  return (
    <section className="f52-panel" aria-labelledby="f52-h-act" data-testid="dashboard-audit-panel">
      <div className="f52-panel-head">
        <h2 id="f52-h-act">{t52("actTitle")}</h2>
        {can("audit") && <Link to="/audit" className="f52-btn f52-btn-secondary" data-testid="dashboard-link-audit"><ScrollText aria-hidden="true" />{t52("actOpen")}</Link>}
      </div>
      {!stats ? <Notice text={t52("loadError")} testid="dashboard-stats-error" /> : !rows.length ? (
        <Empty52 icon={ScrollText} tone="neutral" title={t52("actNone")} testid="dashboard-audit-empty" />
      ) : (
        <>
          <table className="f52-act" data-testid="dashboard-audit-table">
            <thead><tr><th className="t-time" scope="col">{t52("thTime")}</th><th className="t-user" scope="col">{t52("thUser")}</th><th className="t-kind" scope="col">{t52("thAction")}</th><th scope="col">{t52("thEvent")}</th></tr></thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} data-testid={`dashboard-audit-row-${a.id}`}>
                  <td className="t-time">{fmt(a.timestamp) || "—"}</td>
                  <td className="t-user" title={a.user_email}>{a.user_email || "—"}</td>
                  <td className="t-kind">{a.action || "—"}</td>
                  <td><span className="f52-ev" title={a.detail}>{a.detail || "—"}<small>{a.target}</small></span></td>
                </tr>
              ))}
            </tbody>
          </table>
          <ul className="f52-act-list" data-testid="dashboard-audit-list">
            {rows.map((a) => (
              <li key={a.id}>
                <div className="row1"><span>{fmt(a.timestamp) || "—"}</span><span>{a.action}</span></div>
                <p>{a.detail || "—"}</p>
                <div className="who">{[a.user_email, a.target].filter(Boolean).join(" · ")}</div>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
